import { useEffect, useState } from 'react';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Empty, Loading } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { applyWebTheme } from '../../theme/applyTheme';

/**
 * Theme picker for the website.
 *
 * Each card previews the theme in its own colours - a name alone tells you
 * nothing. Choosing one repaints the site immediately (CSS variables) and
 * saves to the profile so the phone app matches on next open.
 */
const AppearancePage = () => {
  const toast = useToast();
  const { user, setUser } = useAuth();
  const [themes, setThemes] = useState([]);
  const [allow, setAllow] = useState(true);
  const [adminDefault, setAdminDefault] = useState('kaamwala');
  const [active, setActive] = useState(user?.themeId || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.app.config()
      .then((r) => {
        const a = r.data.appearance || {};
        setThemes(a.themes || []);
        setAllow(a.allowUserThemes !== false);
        setAdminDefault(a.themeId || 'kaamwala');
        setActive(user?.themeId || a.themeId || 'kaamwala');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const choose = async (t) => {
    setActive(t.id);
    applyWebTheme(t);
    try {
      const res = await api.user.update({ themeId: t.id });
      if (setUser) setUser(res.data);
      toast.success(`${t.name} applied`);
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <Loading text="Loading themes…" />;

  return (
    <>
      <PageHeader title="Appearance" subtitle="Choose how KAAMWALA looks for you" />

      {!allow ? (
        <Empty emoji="🎨" title="Theme is set by KAAMWALA" text="Your account follows the brand theme right now." />
      ) : (
        <div className="grid cols-2" style={{ gap: 14 }}>
          {themes.map((t, i) => (
            <Reveal key={t.id} delay={stagger(i, 40, 240)}>
              <button
                type="button"
                onClick={() => choose(t)}
                className={`card press${active === t.id ? ' sh-md' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                  cursor: 'pointer', border: `2px solid ${active === t.id ? t.colors.primary : 'transparent'}`,
                }}
              >
                {/* Preview painted in the theme's own palette. */}
                <span style={{
                  width: 92, height: 68, borderRadius: 12, overflow: 'hidden', flex: '0 0 auto',
                  background: t.colors.bg, border: `1px solid ${t.colors.line}`, display: 'block',
                }}>
                  <span style={{ display: 'block', height: 20, background: t.colors.primary }} />
                  <span style={{ display: 'block', padding: 8 }}>
                    <span style={{ display: 'block', height: 5, width: '65%', borderRadius: 3, background: t.colors.ink }} />
                    <span style={{ display: 'block', height: 5, width: '42%', borderRadius: 3, background: t.colors.muted, marginTop: 5 }} />
                    <span style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                      <span style={{ width: 16, height: 9, borderRadius: 4, background: t.colors.primary }} />
                      <span style={{ width: 16, height: 9, borderRadius: 4, background: t.colors.accent }} />
                    </span>
                  </span>
                </span>

                <span className="grow">
                  <strong style={{ display: 'block' }}>
                    {t.name}
                    {t.dark ? <span className="t-tiny" style={{ marginLeft: 6 }}>· dark</span> : null}
                    {t.id === adminDefault ? <span className="t-tiny" style={{ marginLeft: 6 }}>· default</span> : null}
                  </strong>
                  <span className="t-tiny">{t.description}</span>
                </span>

                <span style={{
                  width: 22, height: 22, borderRadius: 11, flex: '0 0 auto',
                  border: `${active === t.id ? 7 : 2}px solid ${active === t.id ? t.colors.primary : 'var(--line)'}`,
                }}
                />
              </button>
            </Reveal>
          ))}
        </div>
      )}
    </>
  );
};

export default AppearancePage;
